import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { distinctUntilChanged, pairwise, skip } from 'rxjs';
import { createCronValidator } from '../../validators/cron-validator';
import BaseComponent from '../base.component';
import { CronEvent, Frequency, Meridian, FrequencyOptions } from './cron.models';
import { CronService } from './cron.service';
import { TenantScheduleService } from 'src/app/core/services/application/tenant-schedule.service';

@Component({
    selector: 'cdp-cron-editor',
    templateUrl: './cron-editor.component.html',
    styleUrls: ['./cron-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: CronEditorComponent
        }
    ],
    standalone: false
})
export class CronEditorComponent extends BaseComponent implements OnInit {
    @Output() cronChanged = new EventEmitter<CronEvent>(true);
    @Input() controlName!: string;
    @Input() formGroup!: FormGroup;
    form!: FormGroup;
    dayOfWeekOptions = this.cronService.dayOfWeekOptions;
    dayOfMonthOptions = this.cronService.dayOfMonthOptions;

    frequencies = FrequencyOptions;
    frequencyIds = Frequency;
    meridians = Meridian;
    frequencyRange = Array(60);
    minuteRange = Array(60);
    hourRange = Array(12);

    constructor(
        private fb: FormBuilder,
        public cronService: CronService,
        private tenantScheduleService: TenantScheduleService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setDefaultFormValues();
        this.cronService.setFormForCron(this.form, this.formGroup?.controls[this.controlName]?.value);
        this.watchFormChanges();
        this.updateRawCronAsNeeded(this.form.value);
        this.formGroup?.controls[this.controlName]?.valueChanges.pipe(this.unsubscribeOnDestroy()).subscribe(value => {
            if (value !== this.form.value.rawCron) {
                this.cronService.setFormForCron(this.form, this.formGroup?.controls[this.controlName]?.value);
            }
        });
    }

    joinDaysOfWeek(daysOfWeek: string[]): string {
        if (daysOfWeek.length > 1) {
            return daysOfWeek.map(i => i.slice(0, 2)).join(', ');
        } else {
            return daysOfWeek[0];
        }
    }

    displayDaysOfMonth(daysOfMonth: Array<{ value: string; label: string; selectedLabel?: string }>): string {
        if (daysOfMonth.length > 4) {
            return (
                daysOfMonth
                    .slice(0, 3)
                    .map(i => i.selectedLabel || i.label)
                    .join(', ') + `, +${daysOfMonth.length - 3}`
            );
        } else {
            return daysOfMonth.map(i => i.selectedLabel || i.label).join(', ');
        }
    }

    selectAllDaysOfMonth() {
        this.form.controls['daysOfMonth'].setValue(this.dayOfMonthOptions.map(i => i.value));
    }

    unselectAllDaysOfMonth() {
        this.form.controls['daysOfMonth'].setValue([]);
    }

    selectAllDaysOfWeek() {
        this.form.controls['daysOfWeek'].setValue(this.dayOfWeekOptions);
    }

    unselectAllDaysOfWeek() {
        this.form.controls['daysOfWeek'].setValue([]);
    }

    get cronDisplayText(): string {
        const rawCronControl = this.form?.controls['rawCron'];
        const rawCron = rawCronControl?.value;

        if (rawCronControl?.invalid) {
            return this.cronService.CRON_INVALID_MESSAGE;
        }

        return this.cronService.translateCronWithUtc(rawCron);
    }

    private setDefaultFormValues() {
        this.form = this.fb.group({
            frequencyId: new FormControl(Frequency.day),
            frequency: new FormControl('1'),
            daysOfMonth: new FormControl(['1']),
            daysOfWeek: new FormControl(['Monday']),
            hour: new FormControl('6'),
            minute: new FormControl('0'),
            meridian: new FormControl('am'),
            rawCron: new FormControl(this.formGroup?.controls[this.controlName]?.value || '0 6 * * *', {
                validators: [Validators.required],
                asyncValidators: [
                    createCronValidator(expression => this.tenantScheduleService.validateCron(expression))
                ],
                updateOn: 'blur'
            })
        });
    }

    private watchFormChanges() {
        // Emit changes when the raw cron expression changes.
        this.form.controls['rawCron'].valueChanges
            .pipe(this.unsubscribeOnDestroy(), distinctUntilChanged())
            .subscribe(rawCron => {
                this.emitCronExpression(rawCron);
            });

        // Watch for changes in the plain-english form and update the raw cron as needed.

        this.form.valueChanges
            .pipe(this.unsubscribeOnDestroy(), distinctUntilChanged(), skip(1), pairwise())
            .subscribe(([prev, next]) => {
                if (prev.rawCron === next.rawCron) {
                    this.updateRawCronAsNeeded(next);
                }
            });
    }

    private updateRawCronAsNeeded(data: any) {
        // check for update via plain-english frequency form
        const expression = this.cronService.getCronExpression(data);
        if (expression) {
            // Update the expression only if it has changed.
            if (expression !== data.rawCron) {
                setTimeout(() => {
                    this.form.controls['rawCron'].setValue(expression);
                }, 0);
            }
            this.emitCronExpression(expression);
        }
    }

    private emitCronExpression(expression: string) {
        this.cronChanged.emit({
            rawCron: expression,
            formGroup: this.formGroup,
            controlName: this.controlName
        });
    }
}
